import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Switch,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Google as GoogleIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import EventsList from '../components/EventsList';
import { apiService } from '../services/apiService';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';

interface CalendarIntegration {
  id: string;
  provider: string;
  email: string;
  calendarId: string;
  syncStatus: 'active' | 'paused' | 'error';
  lastSyncedAt?: string;
  lastError?: string;
  createdAt: string;
}

interface CalendarSettings {
  autoJoinEnabled: boolean;
  minutesBeforeStart: number | '';
  botName: string;
  enabledPlatforms: string[];
}

const Calendar: React.FC = () => {
  const [integrations, setIntegrations] = useState<CalendarIntegration[]>([]);
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>({
    autoJoinEnabled: false,
    minutesBeforeStart: 5,
    botName: 'Sales Guru AI',
    enabledPlatforms: ['meet', 'zoom', 'teams'],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      setError(null);

      const integrationsResponse = (await apiService.get('/calendar/integrations')) as any;
      if (integrationsResponse.success) {
        setIntegrations(integrationsResponse.integrations || []);
      }

      const settingsResponse = (await apiService.get('/calendar/settings')) as any;
      if (settingsResponse.success && settingsResponse.settings) {
        setCalendarSettings({
          autoJoinEnabled: settingsResponse.settings.autoJoinEnabled ?? false,
          minutesBeforeStart: settingsResponse.settings.minutesBeforeStart ?? 5,
          botName: settingsResponse.settings.botName ?? 'Sales Guru AI',
          enabledPlatforms: settingsResponse.settings.enabledPlatforms ?? ['meet', 'zoom', 'teams'],
        });
      }
    } catch (err: any) {
      console.error('Error fetching calendar data:', err);
      setError(err.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      setConnecting(true);
      setError(null);

      const response = (await apiService.get('/calendar/auth/google')) as any;

      if (response.success && response.authUrl) {
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
          response.authUrl,
          'Google Calendar OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        const checkPopup = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(checkPopup);
            fetchCalendarData();
            setConnecting(false);
          }
        }, 500);
      } else {
        setError(response.error || 'Failed to initiate OAuth');
        setConnecting(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect calendar');
      setConnecting(false);
    }
  };

  const handleDisconnect = async (integrationId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect this calendar? All scheduled bots will be cancelled.'
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.delete(`/calendar/integrations/${integrationId}`);

      if (response.success) {
        setSuccess('Calendar disconnected successfully');
        setTimeout(() => setSuccess(null), 3000);
        fetchCalendarData();
      } else {
        setError(response.error || 'Failed to disconnect calendar');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const settingsToSave = {
        ...calendarSettings,
        minutesBeforeStart:
          typeof calendarSettings.minutesBeforeStart === 'string'
            ? 5
            : calendarSettings.minutesBeforeStart,
      };

      const { getValidToken } = await import('../utils/tokenUtils');
      const { apiConfig } = await import('../config');

      const token = await getValidToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiConfig.baseURL}/api/calendar/settings`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(settingsToSave),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Calendar settings updated successfully');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(result.error || 'Failed to save settings');
      }
    } catch (err: any) {
      console.error('Error saving calendar settings:', err);
      setError(err.message || 'Failed to save calendar settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  return (
    <MainLayout title="Calendar Integration">
      <div className="p-2">
        {/* Page Header */}
        <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-900">Calendar Integration</h1>
        <p className="text-gray-600 mt-1">Connect your calendar to automatically join meetings with AI assistant</p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <div className="space-y-6">
        {/* Connected Calendars */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            maxWidth: '800px',
          }}
          data-tutorial="calendar-integrations"
        >
          <Box className="flex items-center justify-between mb-4">
            <Box className="flex items-center gap-2">
              <CalendarIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6">Connected Calendars</Typography>
            </Box>
            <IconButton onClick={fetchCalendarData} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box className="flex justify-center py-6">
              <CircularProgress />
            </Box>
          ) : integrations.length > 0 ? (
            <div className="space-y-2 mb-4">
              {integrations.map((integration) => (
                <Box
                  key={integration.id}
                  sx={{
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    p: 2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                  }}
                >
                  <Box className="flex items-center gap-2">
                    <GoogleIcon sx={{ color: '#4285F4', fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {integration.email}
                      </Typography>
                      <Box className="flex items-center gap-2 mt-0.5">
                        <Chip
                          icon={
                            integration.syncStatus === 'active' ? (
                              <CheckCircleIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ErrorIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          label={integration.syncStatus === 'active' ? 'Connected' : 'Error'}
                          size="small"
                          color={integration.syncStatus === 'active' ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                        />
                        {integration.lastSyncedAt && (
                          <Typography variant="caption" color="textSecondary">
                            Synced: {formatDate(integration.lastSyncedAt)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => handleDisconnect(integration.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </div>
          ) : (
            <Box
              sx={{
                backgroundColor: 'grey.50',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                mb: 3,
              }}
            >
              <CalendarIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                No calendars connected. Connect your Google Calendar to enable automatic bot
                scheduling.
              </Typography>
            </Box>
          )}

          <Box className="flex justify-end">
            <Button
              variant="contained"
              onClick={handleConnectGoogle}
              disabled={connecting || loading}
              startIcon={<GoogleIcon />}
              size="small"
            >
              {connecting ? 'Connecting...' : 'Connect Google Calendar'}
            </Button>
          </Box>
        </Box>

        {/* Auto-Join Settings */}
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            maxWidth: '800px',
          }}
          data-tutorial="auto-join-settings"
        >
          <Box className="flex items-center gap-2 mb-4">
            <SettingsIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Auto-Join Settings</Typography>
          </Box>

          <div className="space-y-4" style={{ maxWidth: 350 }}>
            {/* Enable Toggle */}
            <Box className="flex items-center justify-between">
              <Typography variant="body2" color="textSecondary">
                Auto-Join Meetings
              </Typography>
              <Switch
                checked={calendarSettings.autoJoinEnabled}
                onChange={(e) =>
                  setCalendarSettings({
                    ...calendarSettings,
                    autoJoinEnabled: e.target.checked,
                  })
                }
                color="primary"
              />
            </Box>

            {/* Minutes Before */}
            <TextField
              fullWidth
              label="Minutes Before Meeting"
              type="number"
              size="small"
              value={calendarSettings.minutesBeforeStart}
              onChange={(e) => {
                const value = e.target.value;
                setCalendarSettings({
                  ...calendarSettings,
                  minutesBeforeStart: value === '' ? ('' as any) : parseInt(value),
                });
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value === '' || isNaN(parseInt(value))) {
                  setCalendarSettings({
                    ...calendarSettings,
                    minutesBeforeStart: 5,
                  });
                } else {
                  const numValue = Math.max(1, Math.min(10, parseInt(value)));
                  setCalendarSettings({
                    ...calendarSettings,
                    minutesBeforeStart: numValue,
                  });
                }
              }}
              helperText="1-10 minutes"
              disabled={!calendarSettings.autoJoinEnabled}
              inputProps={{ min: 1, max: 10 }}
            />

            {/* Bot Name */}
            <TextField
              fullWidth
              label="Bot Display Name"
              size="small"
              value={calendarSettings.botName}
              onChange={(e) =>
                setCalendarSettings({
                  ...calendarSettings,
                  botName: e.target.value,
                })
              }
              helperText="Max 50 characters"
              disabled={!calendarSettings.autoJoinEnabled}
              inputProps={{ maxLength: 50 }}
            />
          </div>

          {/* Save Button - Right aligned */}
          <Box className="flex justify-end mt-4">
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              disabled={saving || loading}
              startIcon={<SaveIcon />}
              size="small"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Box>

        {/* Calendar Events List */}
        {calendarSettings.autoJoinEnabled && integrations.length > 0 && (
          <EventsList autoJoinEnabled={calendarSettings.autoJoinEnabled} />
        )}

        {/* How it Works */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
            borderRadius: 2,
            p: 3,
            maxWidth: '800px',
          }}
        >
          <Box className="flex items-center gap-2 mb-3">
            <InfoIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">How it works</Typography>
          </Box>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { step: '1', text: 'Connect your Google Calendar' },
              { step: '2', text: 'Enable auto-join for meetings' },
              { step: '3', text: 'Bot joins automatically at scheduled time' },
              { step: '4', text: 'Get real-time transcription & AI assistance' },
            ].map((item) => (
              <Box
                key={item.step}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {item.step}
                </Box>
                <Typography variant="body2">{item.text}</Typography>
              </Box>
            ))}
          </div>

          <Typography variant="caption" color="textSecondary" className="mt-3 block">
            Works with Google Meet, Zoom, and Microsoft Teams
          </Typography>
        </Box>
        </div>
      </div>
    </MainLayout>
  );
};

export default Calendar;
